import React, { useMemo, useState } from 'react';
import { Button, Avatar, Tooltip, Modal, message } from 'antd';
import { UserOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import locationIcon from '../../../assets/images/location_icon.png';
import editIcon from '../../../assets/images/edit_icon.svg';
import deleteIcon from '../../../assets/images/delete_icon.svg';
import disableIcon from '../../../assets/images/disable_icon.svg';
import axios from 'axios';
import { API_ENDPOINTS } from '../../../constants/apiEndPoints';
import { useNavigate } from 'react-router-dom';
import { mapProfileStatus, isProfileDisabled, getStatusClassName } from '../../../utils/statusMapping';
import Share from '../../../Common/Share/ShareComponent';


const ProfileHeader = ({
  profileData,
  showImageNavigation = true,
  type = 'profile_single_page',
  profileImagePosition = 'left',
  onProfileUpdate = null
}) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [disableLoading, setDisableLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [shareStatus , setShareStatus] = useState(false);
  const navigate = useNavigate();

  const profileId = useMemo(() => profileData?.id || profileData?._id, [profileData]);
  const isDisabled = useMemo(() => {
    const status = profileData?.status || profileData?.profile_status;
    return isProfileDisabled(status);
  }, [profileData]);

  const renderValue = (value, fallback = 'Not specified') => {
    if (!value) return fallback;
    if (typeof value === 'object' && value.label) return value.label;
    if (typeof value === 'string') return value;
    return fallback;
  };

  const getProfileStatusDisplay = () => {
    const status = profileData?.status || profileData?.profile_status;
    return mapProfileStatus(status);
  };

  const nextImage = () => {
    const images = profileData?.images || profileData?.profile_images;
    if (images && Array.isArray(images) && images.length > 0) {
      setCurrentImageIndex((prev) =>
        prev === images.length - 1 ? 0 : prev + 1
      );
    }
  };

  const prevImage = () => {
    const images = profileData?.images || profileData?.profile_images;
    if (images && Array.isArray(images) && images.length > 0) {
      setCurrentImageIndex((prev) =>
        prev === 0 ? images.length - 1 : prev - 1
      );
    }
  };

  const selectImage = (index) => {
    const images = profileData?.images || profileData?.profile_images;
    if (images && Array.isArray(images) && index >= 0 && index < images.length) {
      setCurrentImageIndex(index);
    }
  };

  const calculateAge = (dob) => {
    if (!dob) return '';
    try {
      // Handle ISO date format from API
      const birthDate = dayjs(dob);
      if (!dayjs.isDayjs(birthDate) || !birthDate.isValid()) return '';
      const today = dayjs();
      return today.diff(birthDate, 'year');
    } catch {
      return '';
    }
  };

  const formatDate = (dob) => {
    if (!dob) return '';
    try {
      // Handle ISO date format from API
      const date = dayjs(dob);
      if (!dayjs.isDayjs(date) || !date.isValid()) return dob;
      return date.format('DD MMM YYYY');
    } catch {
      return dob;
    }
  };

  if (!profileData) {
    return null;
  }

  const handleDisable = () => {
    if (!profileId) {
      message.error('Profile ID not found');
      return;
    }

    Modal.confirm({
      title: 'Disable Profile?',
      content: 'Are you sure you want to disable this profile? This action will hide the profile from public view.',
      okText: 'Disable',
      cancelText: 'Cancel',
      className: 'disable_profile_modal',
      okButtonProps: {
        danger: true,
        loading: disableLoading
      },
      onOk: async () => {
        setDisableLoading(true);
        try {
          await axios.put(API_ENDPOINTS.PROFILE.DISABLE(profileId));
          Modal.success({
            title: 'Profile Disabled Successfully',
            content: 'The profile has been disabled and is no longer visible to other users.',
          });
          // Refresh the profile data if callback is provided
          if (onProfileUpdate && typeof onProfileUpdate === 'function') {
            onProfileUpdate();
          } else {
            // Fallback to page reload if no callback provided
            window.location.reload();
          }
        } catch (err) {
          message.error(err?.response?.data?.message || 'Failed to disable profile');
        } finally {
          setDisableLoading(false);
        }
      }
    });
  };

  const handleEdit = () => {
    if (!profileId) {
      message.error('Profile ID not found');
      return;
    }
    navigate(`/profile-creation/${profileId}`);
  };

  const handleDelete = async () => {
    if (!profileId) {
      message.error('Profile ID not found');
      return;
    }
    Modal.confirm({
      title: 'Delete Profile?',
      content: 'This action cannot be undone.',
      okText: 'Delete',
      okButtonProps: { danger: true, loading: deleteLoading },
      onOk: async () => {
        setDeleteLoading(true);
        try {
          await axios.delete(API_ENDPOINTS.PROFILE.DELETE(profileId));
          message.success('Profile deleted');
          navigate('/my-profiles');
        } catch (err) {
          message.error(err?.response?.data?.message || 'Failed to delete profile');
        } finally {
          setDeleteLoading(false);
        }
      }
    });
  };

  const handleCopyLink = async () => {
    if (!profileId) {
      message.error('Profile ID not found');
      return;
    }
    
    try {
      const baseUrl = window.location.origin;
      const profileLink = `${baseUrl}/public/live-ads/${profileId}`;
      
      await navigator.clipboard.writeText(profileLink);
      message.success('Profile link copied to clipboard!');
      setShareStatus(false); // Close the share menu after copying
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = `${window.location.origin}/public/live-ads/${profileId}`;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        message.success('Profile link copied to clipboard!');
        setShareStatus(false);
      } catch (fallbackErr) {
        message.error('Failed to copy link');
      }
      document.body.removeChild(textArea);
    }
  };

  return (
    <div className={`profile_header ${profileImagePosition}`}>
      {/* <div className="share_wrap">
        <button onClick={()=>{
          setShareStatus((prev)=>!prev)
        }}><img src={shareIcon} alt="" /></button>
        <ul className={`share_list ${shareStatus ? 'active' : ''}`}>
          <li>
            <button onClick={handleCopyLink}><img src={copy_link_icon} alt="" /></button>
            <span className='custom_pointer' onClick={handleCopyLink}>Copy Profile Link</span>
          </li>
          <li>
            <button><img src={gmail_icon} alt="" /></button>
            <span className='custom_pointer'>Gmail</span>
          </li>
          <li>
            <button><img src={wp_icon} alt="" /></button>
            <span className='custom_pointer'>Whatsapp</span>
          </li>
        </ul>
      </div> */}
      <div className="profile_main_info">
        <div className="profile_info_left">
          {type === 'profile_single_page' && (
            <ul className="button_list">
              <li>
                <Tooltip title="Status">
                  <button className={getStatusClassName(profileData?.status || profileData?.profile_status)}>
                    <span>{getProfileStatusDisplay()}</span>
                  </button>
                </Tooltip>
              </li>
              <li>
                <Tooltip title={isDisabled ? 'Profile disabled' : 'Disable profile'}>
                  <button onClick={handleDisable} disabled={isDisabled || disableLoading}>
                    <img src={disableIcon} alt="disable" />
                  </button>
                </Tooltip>
              </li>
              <li>
                <Tooltip title="Edit profile">
                  <button onClick={handleEdit}>
                    <img src={editIcon} alt="edit" />
                  </button>
                </Tooltip>
              </li>
              {/* <li>
                <Tooltip title="Delete profile">
                  <button onClick={handleDelete} disabled={deleteLoading}>
                    <img src={deleteIcon} alt="delete" />
                  </button>
                </Tooltip>
              </li> */}
            </ul>
          )}
          <div className="profile_title">
            <h1 className="profile_name mb-0">{profileData?.fullName || profileData?.name || 'Profile Name'}</h1>
            <span className="age_tag text_sm block">
              {renderValue(profileData?.gender, 'Gender')}, {profileData?.age || calculateAge(profileData?.dob)} yrs / {formatDate(profileData?.dob)}
            </span>
            {/* <div>
              <span className="info_tag text_sm">Basic Information</span>
            </div> */}
          </div>

          <div className="profile_basic_info">
            <span className="info_item">
              {renderValue(profileData?.marital_status || profileData?.maritalStatus, 'Single')} | {renderValue(profileData?.religion || profileData?.religion_label, 'Religion')} | {renderValue(profileData?.caste || profileData?.caste_label, 'Caste')}
            </span>
            <span className="location_info">
              <span className='location_icon'><img src={locationIcon} alt="location" /></span>               {renderValue(
                profileData?.locationData?.displayPlace ||
                profileData?.locationData?.displayName ||
                profileData?.city?.city?.label ||
                profileData?.city?.label ||
                profileData?.city?.display_place ||
                profileData?.location,
                'Location not specified'
              )}
            </span>
          </div>

          <p className="profile_bio">
            {profileData?.bio || 'Lorem ipsum dolor sit amet, consectetur adipiscing elit...'}
          </p>
        </div>

        {/* Profile Image */}
        <div className={`profile_image_section ${profileImagePosition}`}>
          {(() => {
            const images = profileData?.images || profileData?.profile_images;
            return images && images.length > 0 ? (
              <>
                <div className='profile_image_section_inner'>
                  <div className="profile_image_container">
                    <img
                      src={images[currentImageIndex]?.original || images[currentImageIndex]?.thumbnail || images[currentImageIndex]?.url}
                      alt={`${profileData?.name || 'Profile'}`}
                      className="profile_image"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div className="image_placeholder" style={{ display: 'none' }}>
                      <UserOutlined />
                    </div>

                    {showImageNavigation && images.length > 1 && (
                      <div className="image_navigation">
                        <Button
                          shape="circle"
                          size="small"
                          onClick={prevImage}
                          className="nav_btn prev_btn"
                        >
                          <LeftOutlined />
                        </Button>
                        <Button
                          shape="circle"
                          size="small"
                          onClick={nextImage}
                          className="nav_btn next_btn"
                        >
                          <RightOutlined />
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Thumbnail Slider */}
                  {images.length > 1 && (
                    <div className="thumbnail_slider">
                      {images.map((image, index) => (
                        <div
                          key={index}
                          className={`thumbnail_item ${currentImageIndex === index ? 'active' : ''}`}
                          onClick={() => selectImage(index)}
                        >
                          <img
                            src={image?.original || image?.thumbnail || image?.url}
                            alt={`${profileData?.name || 'Profile'} thumbnail ${index + 1}`}
                            className="thumbnail_image"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                          <div className="thumbnail_placeholder" style={{ display: 'none' }}>
                            <UserOutlined />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="profile_image_placeholder">
                <Avatar size={200} icon={<UserOutlined />} />
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
};

ProfileHeader.propTypes = {
  profileData: PropTypes.object,
  showImageNavigation: PropTypes.bool
};

ProfileHeader.defaultProps = {
  profileData: null,
  showImageNavigation: true
};

export default ProfileHeader;
